import { Browser } from '@capacitor/browser';
import { isNative } from './is-native';

/**
 * Open an OAuth URL in the system browser sheet — SFSafariViewController on
 * iOS, Chrome Custom Tabs on Android. Google + Microsoft both require system
 * browsers for OAuth and refuse to authenticate from embedded WebViews, so
 * this is the only acceptable primitive.
 *
 * Resolves when the user closes the sheet. The caller should then refresh
 * server-side connection state to determine whether the OAuth actually
 * succeeded — Mucka's flow is server-side (the in-app browser hits our
 * /api/integrations/.../callback URL while still in the sheet, which writes
 * the encrypted tokens before redirecting to /integrations/oauth-complete).
 *
 * On non-native platforms this falls back to a full-page redirect, which is
 * fine for desktop/PWA where staying on app.mucka.ai is the right UX.
 */
export interface OAuthSessionOptions {
  /**
   * Asked every couple of seconds while the sheet is open. When it answers
   * true the sheet closes itself, so somebody who has just connected is not
   * left on a "you can close this now" page hunting for Done.
   *
   * 🚨 A programmatic `Browser.close()` does NOT emit `browserFinished` on iOS
   * (the plugin only notifies from `safariViewControllerDidFinish`, which is
   * the Done button), so the session resolves itself on that path rather than
   * waiting for an event that never comes.
   */
  closeWhen?: () => Promise<boolean>;
}

/** How often `closeWhen` is asked. */
const CLOSE_POLL_MS = 2_000;

export async function openOAuthSession(url: string, options: OAuthSessionOptions = {}): Promise<void> {
  if (!isNative()) {
    if (typeof window !== 'undefined') window.location.href = url;
    return;
  }

  return new Promise<void>(async (resolve) => {
    let done = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = async () => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      await handle.remove();
      resolve();
    };

    // Register the close listener BEFORE opening so we don't miss the event
    // on a very-fast close (user backs out immediately).
    const handle = await Browser.addListener('browserFinished', () => void finish());
    await Browser.open({ url });

    const { closeWhen } = options;
    if (!closeWhen) return;
    const poll = async () => {
      if (done) return;
      const ready = await closeWhen().catch(() => false);
      if (done) return;
      if (ready) {
        await Browser.close().catch(() => {});
        await finish();
        return;
      }
      timer = setTimeout(poll, CLOSE_POLL_MS);
    };
    timer = setTimeout(poll, CLOSE_POLL_MS);
  });
}
